import React, { useEffect, useState } from 'react';
import Logo from '../../assets/images/logo.svg';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  useMediaQuery,
  FormControlLabel,
  DialogContentText,
  DialogTitle,
  Checkbox,
  FormGroup,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { investorClassifications, investorTypes } from '../../utils/constants';
import {
  SignupInitialFormData,
  SignupPasswordFormData,
} from '../../types/signup';
import { CustomColors, theme } from '../../theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/Loading';
import axios from 'axios';
import { toast } from 'react-toastify';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PasswordChecklist from 'react-password-checklist';
import PlatformAgreement from '../../components/PlatformAgreement';

const Signup: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState('');
  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption((event.target as HTMLInputElement).value);
  };

  const isSmallScreen = useMediaQuery(
    '(max-width: 900px) and (min-width: 520px)'
  );

  const isMobile = useMediaQuery('(max-width: 520px)');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showOtherInvestorType, setShowOtherInvestorType] = useState(false);
  const [otherInvestorTypeError, setOtherInvestorTypeError] = useState(false);
  const [otherInvestorType, setOtherInvestorType] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupData, setSignupData] = useState<SignupInitialFormData>();

  const [passwordFlow, setPasswordFlow] = useState(false);
  const [passwordFlowError, setPasswordFlowError] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsAccepted2, setTermsAccepted2] = useState(false);
  const [termsAccepted3, setTermsAccepted3] = useState(false);
  const [termsAccepted4, setTermsAccepted4] = useState(false);

  const handleTermsCheckboxChange = () => {
    setTermsAccepted(!termsAccepted);
  };
  const handleTerms2CheckboxChange = () => {
    setTermsAccepted2(!termsAccepted2);
  };
  const handleTerms3CheckboxChange = () => {
    setTermsAccepted3(!termsAccepted3);
  };
  const handleTerms4CheckboxChange = () => {
    setTermsAccepted4(!termsAccepted4);
  };

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [readTerms, setReadTerms] = useState(true);

  const handleOpenTermsModal = () => {
    setTermsModalOpen(true);
  };

  const descriptionElementRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    if (termsModalOpen) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [termsModalOpen]);

  const handleCloseTermsModal = () => {
    if (readTerms) {
      setTermsModalOpen(false);
    } else {
      console.error(
        'Please read and accept the terms before closing the modal.'
      );
    }
  };

  const handleCheckboxChange = () => {
    setReadTerms(!readTerms);
  };

  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    watch: watchInitial,
  } = useForm<SignupInitialFormData>();

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    watch,
  } = useForm<SignupPasswordFormData>();

  const investorType = watchInitial('investorType');

  useEffect(() => {
    if (investorType === 'Other Please state') {
      setShowOtherInvestorType(true);
    } else {
      setOtherInvestorType('');
      setShowOtherInvestorType(false);
    }
  }, [investorType]);

  const onSubmit: SubmitHandler<SignupInitialFormData> = async (data) => {
    setEmailExistsError(false);
    setOtherInvestorTypeError(false);
    setLoading(true);

    if (
      !termsAccepted ||
      !termsAccepted3 ||
      !termsAccepted4 ||
      selectedOption === ''
    ) {
      toast.error('Please accept the terms and conditions');
      setLoading(false);
      return;
    }

    if (investorType === 'Other Please state') {
      if (otherInvestorType === '') {
        setOtherInvestorTypeError(true);
        setLoading(false);
        return;
      }
      data.investorType = otherInvestorType;
    }

    if (Object.keys(errors).length === 0) {
      const emailExists = await checkEmailExists(data.email);

      if (emailExists) {
        toast.error('Email already exists');
        setError('email', {
          type: 'manual',
          message: 'Email already exists. Please use a different email.',
        });
        setEmailExistsError(true);
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/signup`,
        {
          ...data,
          representativeOfOrg: selectedOption,
        }
      );

      if (response.status === 201) {
        setShowPasswordModal(false);
        setShowLoader(true);
        setLoading(false);

        setTimeout(() => {
          navigate('/waiting');
        }, 3000);
      } else {
        setLoading(false);
        toast.error('Something went wrong');
      }
    }
    setLoading(false);
    return;
  };

  const handleOtherInvestorType = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setOtherInvestorType(e.target.value);
  };

  const checkEmailExists = async (email: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/email-exists`,
        { email }
      );

      return response.data;
    } catch (error) {
      console.error('Error checking email existence:', error);
      return false;
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    h1: {
      fontSize: theme.spacing(5),
      lineHeight: theme.spacing(5),
      fontWeight: 500,
      marginLeft: isSmallScreen ? 0 : isMobile ? 0 : '100px',
      width: isSmallScreen ? '50%' : '100%',
      color: CustomColors.foregroundColor,
    },
    p: {
      width: isSmallScreen ? '50%' : '100%',
      marginTop: 3,
      marginLeft: isSmallScreen ? 0 : isMobile ? 0 : '100px',
      color: CustomColors.grey,
    },

    container: {
      marginTop: theme.spacing(8),
      padding: theme.spacing(5),
    },
    mainContainer: {
      padding: isSmallScreen ? '50px' : 0,
    },
    signUpContainer: {
      marginTop: isSmallScreen ? 0 : isMobile ? 0 : theme.spacing(8),
      paddingRight: isSmallScreen ? 0 : isMobile ? 0 : '150px',
    },
    logo: {
      width: theme.spacing(20),
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
    button: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
  };

  const handleFormSubmit: SubmitHandler<SignupPasswordFormData> = async (
    data
  ) => {
    setLoading(true);
    if (!passwordFlow) {
      setPasswordFlowError(true);
      return;
    }

    try {
      if (
        Object.keys(passwordErrors).length === 0 &&
        Object.keys(errors).length === 0
      ) {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/user/signup`,
          {
            ...signupData,
            ...data,
          }
        );

        if (response.status === 201) {
          setShowPasswordModal(false);
          setShowLoader(true);

          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setLoading(false);
          toast.error('Something went wrong');
        }
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      toast.error('Something went wrong');
    }

    setLoading(false);
    return;
  };

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  return (
    <div>
      <Container maxWidth="xl">
        <img src={Logo} alt="BlueSana" style={styles.logo} />

        <Grid container spacing={2} sx={styles.mainContainer}>
          <Grid item xs={12} md={6} sx={styles.container}>
            <Grid container>
              <Grid item xs={12} md={2}></Grid>
              <Grid item xs={12} md={6} sx={styles.text}>
                <Typography variant="h1" sx={styles.h1}>
                  TELL US ABOUT YOURSELF
                </Typography>

                <Typography sx={styles.p}>
                  Create a free online account with BlueSana and explore
                  private-market climate investment opportunities.
                  <br /> The more you can tell us about yourself in the comments
                  box the faster we can approve your application and grant
                  access to the BlueSana platform.
                  <br />
                  If you have a Linkedin profile please include this.
                  <br />
                  Please remember that the BlueSana platform is only available
                  to wholesale investors.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} sx={styles.signUpContainer}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      name="firstName"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: 'First Name is required',
                        minLength: {
                          value: 2,
                          message: 'First Name must be at least 2 characters',
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label="First Name"
                            variant="outlined"
                            error={!!errors.firstName}
                          />
                          {errors.firstName && (
                            <FormHelperText error>
                              {errors.firstName.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      name="lastName"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: 'Last Name is required',
                        minLength: {
                          value: 2,
                          message: 'Last Name must be at least 2 characters',
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label="Last Name"
                            variant="outlined"
                            error={!!errors.lastName}
                          />
                          {errors.lastName && (
                            <FormHelperText error>
                              {errors.lastName.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Email"
                        variant="outlined"
                        error={!!errors.email}
                      />
                      {errors.email && (
                        <FormHelperText error>
                          {errors.email.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Phone is required',
                    pattern: {
                      value: /^(\+\d{1,3})?\d{8,14}$/,
                      message: 'Invalid phone number',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Phone"
                        variant="outlined"
                        error={!!errors.phone}
                      />
                      {errors.phone && (
                        <FormHelperText error>
                          {errors.phone.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Investor Type</InputLabel>
                <Controller
                  name="investorType"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Investor Type is required' }}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        variant="outlined"
                        label="Investor Type"
                      >
                        {investorTypes.map((type: string) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.investorType && (
                        <FormHelperText error>
                          {errors.investorType.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              {showOtherInvestorType && (
                <FormControl fullWidth margin="normal">
                  <TextField
                    label="Other Investor Type"
                    onChange={handleOtherInvestorType}
                    value={otherInvestorType}
                    variant="outlined"
                  />
                  {otherInvestorTypeError && (
                    <FormHelperText error>
                      {"Please enter the investor's type"}
                    </FormHelperText>
                  )}
                </FormControl>
              )}

              {/* <FormControl fullWidth margin="normal">
                <InputLabel>Investor Classification</InputLabel>
                <Controller
                  name="investorClassification"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Investor Classification is required' }}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        variant="outlined"
                        label="Investor Classification"
                      >
                        {investorClassifications.map((type: string) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.investorClassification && (
                        <FormHelperText error>
                          {errors.investorClassification.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl> */}

              <FormControl fullWidth margin="normal">
                <Controller
                  name="entityName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Company Name"
                        variant="outlined"
                      />
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="location"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Company Location"
                        variant="outlined"
                      />
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="entityWebAddress"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^(www\.)?[^ "]+$/,
                      message: 'Invalid URL format',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Company Web Address"
                        variant="outlined"
                      />
                      {errors.entityWebAddress && (
                        <FormHelperText error>
                          {errors.entityWebAddress.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="linkedInProfile"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?linkedin.com\/[^ "]+$/,
                      message: 'Invalid LinkedIn Profile URL format',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="LinkedIn Profile"
                        variant="outlined"
                      />
                      {errors.linkedInProfile && (
                        <FormHelperText error>
                          {errors.linkedInProfile.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="comment"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Comment is required',
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Comment"
                        rows={4}
                        multiline
                        variant="outlined"
                        error={!!errors.comment}
                      />
                      <FormHelperText
                        style={{ color: CustomColors.foregroundColor }}
                      >
                        Please add any other relevant information and background
                        on why you want access to the BlueSana platform.
                      </FormHelperText>
                      {errors.comment && (
                        <FormHelperText error>
                          {errors.comment.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>
              <p>
                Before registering for BlueSana platform access, carefully read
                the below and confirm by ticking the appropriate boxes below:
              </p>

              <FormControlLabel
                sx={{ my: 2 }}
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={handleTermsCheckboxChange}
                    color="secondary"
                    sx={{
                      color: CustomColors.grey,
                      display: 'flex',
                      alignSelf: 'baseline',
                    }}
                  />
                }
                label={
                  <span>
                    You represent that You, or, if applicable, the organisation
                    you represent, is either:
                  </span>
                }
              />

              <ul>
                <li>
                  a wholesale client as defined in section 761G of the
                  Corporations Act 2001 (Cth); or
                </li>
                <li>
                  if located outside Australia, is a person to whom an
                  invitation or offer to subscribe for securities, and any issue
                  of such securities is permitted without the need for any
                  registration, lodgement or other formality by the laws of the
                  jurisdiction in which they are situated.
                </li>
              </ul>

              <FormControlLabel
                sx={{ my: 2 }}
                control={
                  <Checkbox
                    checked={termsAccepted3}
                    onChange={handleTerms3CheckboxChange}
                    color="secondary"
                    sx={{
                      color: CustomColors.grey,
                      display: 'flex',
                      alignSelf: 'baseline',
                    }}
                  />
                }
                label={
                  <span>
                    You agree to advise BlueSana of any participation in an
                    offering presented on the BlueSana Platform within 14 days
                    of such participation and or a request from BlueSana.
                  </span>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted4}
                    onChange={handleTerms4CheckboxChange}
                    color="secondary"
                    sx={{
                      color: CustomColors.grey,
                      display: 'flex',
                      alignSelf: 'baseline',
                    }}
                  />
                }
                label={
                  <span>
                    You have read and accept the terms of the{' '}
                    <span
                      style={{
                        color: CustomColors.foregroundColor,
                        cursor: 'pointer',
                      }}
                      onClick={handleOpenTermsModal}
                    >
                      Platform User Agreement.
                    </span>
                  </span>
                }
              />

              <FormControl style={{ marginTop: theme.spacing(2) }}>
                <FormLabel
                  style={{ color: CustomColors.black }}
                  id="demo-radio-buttons-group-label"
                >
                  Are you registering as the representative of an organisation?
                </FormLabel>
                <RadioGroup
                  aria-label="select-option"
                  name="select-option"
                  value={selectedOption}
                  onChange={handleOptionChange}
                >
                  <FormControlLabel
                    value="Yes"
                    control={
                      <Radio
                        color="secondary"
                        sx={{
                          color: CustomColors.grey,
                          display: 'flex',
                          alignSelf: 'baseline',
                        }}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    value="No"
                    control={
                      <Radio
                        color="secondary"
                        sx={{
                          color: CustomColors.grey,
                          display: 'flex',
                          alignSelf: 'baseline',
                        }}
                      />
                    }
                    label="No"
                  />
                </RadioGroup>
              </FormControl>

              {selectedOption === 'Yes' && (
                <FormControlLabel
                  sx={{ my: 2 }}
                  control={
                    <Checkbox
                      checked={termsAccepted2}
                      onChange={handleTerms2CheckboxChange}
                      color="secondary"
                      sx={{
                        color: CustomColors.grey,
                        display: 'flex',
                        alignSelf: 'baseline',
                      }}
                    />
                  }
                  label={
                    <span>
                      If you are registering as the representative of an
                      organisation: You have the authority of your organisation
                      to create this User Account and agree to the{' '}
                      <span
                        style={{
                          color: CustomColors.foregroundColor,
                          cursor: 'pointer',
                        }}
                        onClick={handleOpenTermsModal}
                      >
                        Platform User Agreement.
                      </span>{' '}
                      on their behalf.
                    </span>
                  }
                />
              )}

              {(errors.firstName ||
                errors.lastName ||
                errors.email ||
                errors.phone ||
                errors.investorType) && (
                <FormHelperText error>
                  {termsAccepted && termsAccepted3 && termsAccepted4
                    ? ''
                    : 'Please ensure you tick all the boxes before continuing.'}
                </FormHelperText>
              )}

              <Dialog
                open={termsModalOpen}
                onClose={handleCloseTermsModal}
                maxWidth="lg"
                scroll={'paper'}
              >
                <DialogTitle>Terms and Conditions</DialogTitle>
                <DialogContent dividers={true}>
                  <DialogContentText
                    sx={{ color: '#000' }}
                    id="scroll-dialog-description"
                    ref={descriptionElementRef}
                    tabIndex={-1}
                  >
                    <PlatformAgreement />
                  </DialogContentText>
                </DialogContent>

                <DialogActions>
                  <Button onClick={handleCloseTermsModal} color="secondary">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
              <br />

              <Button
                style={styles.button}
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={
                  loading ||
                  !termsAccepted ||
                  !termsAccepted3 ||
                  !termsAccepted4 ||
                  selectedOption === '' ||
                  (selectedOption === 'Yes' && !termsAccepted2)
                }
                endIcon={<ChevronRightIcon />}
              >
                Signup
              </Button>
            </form>
          </Grid>
        </Grid>
      </Container>

      {showLoader && (
        <Loading heading="Account Created!" text="Please Wait...." />
      )}

      {/* <Dialog
        aria-labelledby="customized-dialog-title"
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      >
        <form onSubmit={handlePasswordSubmit(handleFormSubmit)}>
          <DialogContent dividers>
            <Typography variant="h5" gutterBottom>
              Thanks
              <span
                style={{
                  color: CustomColors.foregroundColor,
                  marginLeft: theme.spacing(1),
                }}
              >
                {signupData?.firstName + ' ' + signupData?.lastName}
              </span>
            </Typography>
            <Typography gutterBottom>
              Let’s complete your application by setting up your password.
              Please enter a secure password and you will be redirected to the
              account login page
            </Typography>

            <FormControl fullWidth margin="normal">
              <Controller
                name="password"
                control={passwordControl}
                defaultValue=""
                rules={{
                  required: 'Password is required',
                  validate: (value) =>
                    value === confirmPassword || 'Passwords do not match',
                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^.&+=!]).*$/,
                    message:
                      'Password must meet the criteria: at least 1 lowercase letter, 1 uppercase letter, 1 digit, and 1 special character',
                  },
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      error={!!passwordErrors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {passwordErrors.password && (
                      <FormHelperText error>
                        {passwordErrors.password.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Controller
                name="confirmPassword"
                control={passwordControl}
                defaultValue=""
                rules={{
                  required: 'Confirm Password is required',
                  validate: (value) =>
                    value === password || 'Passwords do not match',
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      label="Confirm Password"
                      type={confirmShowPassword ? 'text' : 'password'}
                      variant="outlined"
                      error={!!passwordErrors.confirmPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setConfirmShowPassword(!confirmShowPassword)
                              }
                            >
                              {confirmShowPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              />
            </FormControl>
            <PasswordChecklist
              rules={['minLength', 'specialChar', 'number', 'capital', 'match']}
              className="text-start"
              minLength={8}
              value={watch('password') || ''}
              valueAgain={watch('confirmPassword') || ''}
              onChange={(isValid) => {
                if (isValid) {
                  setPasswordFlow(true);
                } else {
                  setPasswordFlow(false);
                }
              }}
            />
            {passwordFlowError && (
              <p className="text-danger">
                Password is required and must match the format
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              variant="outlined"
              onClick={() => setShowPasswordModal(false)}
            >
              Close
            </Button>
            <Button
              autoFocus
              variant="contained"
              type="submit"
              disabled={loading}
            >
              Proceed
            </Button>
          </DialogActions>
        </form>
      </Dialog> */}
    </div>
  );
};

export default Signup;
